import { apiClient } from './api'

// Exchange interface matching backend ConversationExchange model with reactive architecture
export interface Exchange {
  id: string
  user_id: string
  project_id?: string
  conversation_id?: string // Nullable in single-thread model
  question_content?: {
    message: string
    type: string
    metadata?: Record<string, any>
  }
  answer_content?: {
    message: string
    metadata?: {
      is_from_transcription?: boolean
      transcription_confidence?: number
      transcription_language?: string
      has_audio?: boolean
      audio_duration?: number
      contains_key_elements?: string[]
    }
  }
  audio_file_url?: string
  processing_status: 'pending' | 'processing' | 'transcribing' | 'complete' | 'failed' |
                    'analyzing_context' | 'determining_next_steps' | 'generating_question' | 'question_generated' | 'ready_for_next' |
                    'analyzing_content' | 'exploring_entities' | 'extracting_elements' | 'generating_response'
  processing?: {
    status: string
    message?: string
    timestamp?: string
  }
  context_relevance: number
  is_session_start: boolean
  is_project_switch: boolean
  session_started_at?: string
  session_recap?: string
  created_at: string
  updated_at: string
  
  // REACTIVE: Rich content structure from API
  content?: {
    // Legacy fields for backwards compatibility
    question?: string
    answer?: string
    has_audio?: boolean
    audio_url?: string
    answer_length?: number
    has_question?: boolean
    
    // Rich DTO content for interactive experiences
    question_dto?: {
      type: string
      message: string
      is_interactive: boolean
      options?: Array<{
        id: string
        title: string
        description?: string
        has_image?: boolean
        image_url?: string
      }>
    }
    
    answer_dto?: {
      message?: string
      has_text: boolean
      has_selections: boolean
      selections?: string[]
      is_from_transcription: boolean
      transcription_confidence?: number
      transcription_language?: string
    }
    
    // Reactive processing context
    answer_context?: any
    next_steps?: any
    extracted_entities?: any
  }
  
  // Backend relationships
  workflows?: Array<{
    id: string
    workflow_type: string
    status: string
  }>
}

// Session information from backend
export interface SessionInfo {
  session_active: boolean
  session_started_at?: string
  current_project_id?: string
  last_exchange_at: string
  session_expired: boolean
  total_exchanges_in_timeline: number
}

// Timeline response from GET /exchanges
export interface TimelineResponse {
  data: Exchange[]
  meta: {
    total: number
    per_page: number
    current_offset: number
    has_more: boolean
  }
  user_thread_info: {
    total_exchanges: number
    current_session_start?: string
    last_project_id?: string
  }
}

// Response from POST /exchanges
export interface MessageResponse {
  exchange_id: string
  message: string
}

/**
 * ExchangeService - Single-Thread Architecture
 * 
 * Handles all API calls for the new exchange-first model.
 * No conversation management needed - direct user thread access.
 */
class ExchangeService {
  /**
   * Send message to user's exchange thread
   * Primary endpoint - replaces conversation-based message sending
   */
  async sendMessage(
    message: string, 
    projectId?: string, 
    options: Record<string, any> = {}
  ): Promise<MessageResponse> {
    try {
      const response = await apiClient.post('/exchanges', {
        message,
        project_id: projectId,
        processing_options: options
      })

      console.log('[ExchangeService] Message sent successfully:', {
        exchange_id: response.data.exchange_id,
        message: response.data.message
      })

      return response.data
    } catch (error: any) {
      console.error('[ExchangeService] Failed to send message:', error.response?.data || error.message)
      throw new Error(error.response?.data?.message || 'Failed to send message')
    }
  }

  /**
   * Send audio message to user's exchange thread
   * Handles multipart/form-data upload
   */
  async sendAudioMessage(
    audioBlob: Blob, 
    projectId?: string, 
    options: Record<string, any> = {}
  ): Promise<MessageResponse> {
    try {
      const formData = new FormData()
      formData.append('audio_file', audioBlob, 'recording.webm')
      
      if (projectId) {
        formData.append('project_id', projectId)
      }
      
      if (Object.keys(options).length > 0) {
        formData.append('processing_options', JSON.stringify(options))
      }

      const response = await apiClient.post('/exchanges', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      console.log('[ExchangeService] Audio message sent successfully:', {
        exchange_id: response.data.exchange_id,
        message: response.data.message
      })

      return response.data
    } catch (error: any) {
      console.error('[ExchangeService] Failed to send audio message:', error.response?.data || error.message)
      throw new Error(error.response?.data?.message || 'Failed to send audio message')
    }
  }

  /**
   * Get user's exchange timeline
   * Primary view - replaces conversation-based exchange loading
   */
  async getTimeline(
    limit = 20, 
    offset = 0, 
    includeWorkflows = false
  ): Promise<TimelineResponse> {
    try {
      const response = await apiClient.get('/exchanges', {
        params: { 
          limit, 
          offset, 
          include_workflows: includeWorkflows 
        }
      })

      console.log('[ExchangeService] Timeline loaded:', {
        total: response.data.meta.total,
        loaded: response.data.data.length,
        has_more: response.data.meta.has_more
      })

      return response.data
    } catch (error: any) {
      console.error('[ExchangeService] Failed to load timeline:', error.response?.data || error.message)
      throw new Error(error.response?.data?.message || 'Failed to load timeline')
    }
  }

  /**
   * Get current session information
   * Shows session status, activity, timeline stats
   */
  async getCurrentSession(): Promise<SessionInfo> {
    try {
      const response = await apiClient.get('/exchanges/session')

      console.log('[ExchangeService] Session info loaded:', {
        session_active: response.data.session_active,
        total_exchanges: response.data.total_exchanges_in_timeline
      })

      return response.data
    } catch (error: any) {
      console.error('[ExchangeService] Failed to get session info:', error.response?.data || error.message)
      throw new Error(error.response?.data?.message || 'Failed to get session info')
    }
  }

  /**
   * Utility: Check if exchange is user message
   */
  isUserMessage(exchange: Exchange): boolean {
    return !!exchange.answer_content && !exchange.question_content
  }

  /**
   * Utility: Check if exchange is AI question
   */
  isAiMessage(exchange: Exchange): boolean {
    return !!exchange.question_content
  }

  /**
   * Utility: Get display content for exchange
   */
  getDisplayContent(exchange: Exchange): string {
    return exchange.answer_content?.message || exchange.question_content?.message || ''
  }

  /**
   * Utility: Get processing status display
   */
  getProcessingStatusDisplay(status: Exchange['processing_status']): {
    label: string
    color: string
    icon: string
  } {
    const statusMap = {
      pending: { label: 'Processing...', color: 'text-yellow-600', icon: '⏳' },
      processing: { label: 'Thinking...', color: 'text-blue-600', icon: '🧠' },
      transcribing: { label: 'Transcribing...', color: 'text-purple-600', icon: '🎙️' },
      complete: { label: 'Complete', color: 'text-green-600', icon: '✅' },
      failed: { label: 'Failed', color: 'text-red-600', icon: '❌' },
      analyzing_context: { label: 'Analyzing Context...', color: 'text-blue-500', icon: '🔍' },
      determining_next_steps: { label: 'Planning Next Steps...', color: 'text-indigo-600', icon: '🎯' },
      generating_question: { label: 'Generating Question...', color: 'text-purple-500', icon: '❓' },
      question_generated: { label: 'I have a question for you!', color: 'text-green-600', icon: '✅' },
      ready_for_next: { label: 'Ready for Next', color: 'text-green-500', icon: '▶️' },
      analyzing_content: { label: 'Analyzing Content...', color: 'text-cyan-600', icon: '📋' },
      exploring_entities: { label: 'Exploring Entities...', color: 'text-orange-600', icon: '🔗' },
      extracting_elements: { label: 'Extracting Elements...', color: 'text-teal-600', icon: '⚗️' },
      generating_response: { label: 'Generating Response...', color: 'text-violet-600', icon: '✨' }
    }
    
    return statusMap[status] || statusMap.pending
  }
}

// Export singleton instance
export const exchangeService = new ExchangeService()
export default exchangeService